import { useState, useCallback, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import {
    Box,
    Button,
    Step,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import Modal from '~/components/Modal';
import MultiPatientConflictDialog from '~/components/Dialogs/MultiPatientConflictDialog';
import { dicomSourcesModalOpenState } from '~/state/atoms/viewerAtoms';
import useDicomImport from '~/hooks/useDicomImport';
import usePrimaryImage from '~/hooks/image/usePrimaryImage';
import { STEPS } from './types';
import StepSearchPatients from './StepSearchPatients';
import StepSelectSeries from './StepSelectSeries';
import StepImportComplete from './StepImportComplete';

export default function DicomSourcesModal() {
    const [open, setOpen] = useRecoilState(dicomSourcesModalOpenState);
    const [activeStep, setActiveStep] = useState(0);
    const [sortBy, setSortBy] = useState<string>('Name');
    const [expandedPatients, setExpandedPatients] = useState<
        Record<string, boolean>
    >({});
    const { availableImages } = usePrimaryImage(true);

    const handleClose = useCallback(() => {
        setOpen(false);
        setActiveStep(0);
        setExpandedPatients({});
    }, [setOpen]);

    // Skip primary image selection when only one image
    // series is loaded — nothing to choose
    useEffect(() => {
        if (activeStep === 2 && availableImages.length <= 1) {
            setOpen(false);
            setActiveStep(0);
            setExpandedPatients({});
        }
    }, [activeStep, availableImages.length, setOpen]);

    const dicom = useDicomImport({
        onImportComplete: () => setActiveStep(2),
    });

    const handleStepClick = useCallback(
        (step: number) => {
            if (step < activeStep) {
                setActiveStep(step);
                if (step === 0) {
                    dicom.handleBackToSearch();
                }
            }
        },
        [activeStep, dicom]
    );

    const toggleExpanded = useCallback((patientId: string) => {
        setExpandedPatients((prev) => ({
            ...prev,
            [patientId]: !prev[patientId],
        }));
    }, []);

    const handleSelectPatient = useCallback(
        (patient: Parameters<typeof dicom.handleSelectPatient>[0]) => {
            dicom.handleSelectPatient(patient);
            setActiveStep(1);
        },
        [dicom]
    );

    const handleBackToSearch = useCallback(() => {
        dicom.handleBackToSearch();
        setActiveStep(0);
    }, [dicom]);

    const handleModalClose = useCallback(() => {
        dicom.resetState();
        handleClose();
    }, [dicom, handleClose]);

    const activeConflict = dicom.earlyConflict.open
        ? dicom.earlyConflict
        : dicom.patientConflict;
    const resolveConflict = dicom.earlyConflict.open
        ? dicom.resolveEarlyConflict
        : dicom.resolvePatientConflict;

    return (
        <>
            <MultiPatientConflictDialog
                open={activeConflict.open}
                loadedPatientId={activeConflict.loadedPatientId}
                incomingPatientIds={activeConflict.incomingPatientIds}
                hasUnsavedChanges={activeConflict.hasUnsavedChanges}
                clinicalUse={activeConflict.clinicalUse}
                onAction={resolveConflict}
            />
            <Modal open={open} onClose={handleModalClose}>
                <Box
                    display="flex"
                    flexDirection="column"
                    width="70vw"
                    maxWidth="1000px"
                    height="60vh"
                    maxHeight="600px"
                >
                    <Box pb={2}>
                        <Typography variant="h6">
                            <FormattedMessage id="loadFromDicomSources" />
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <FormattedMessage id="searchAndLoadPatients" />
                        </Typography>
                    </Box>

                    <Box pb={2}>
                        <Stepper activeStep={activeStep} alternativeLabel>
                            {STEPS.map((translationKey, index) => (
                                <Step key={translationKey}>
                                    <StepLabel
                                        onClick={() => handleStepClick(index)}
                                        sx={{
                                            cursor:
                                                index < activeStep
                                                    ? 'pointer'
                                                    : 'default',
                                            '&:hover':
                                                index < activeStep
                                                    ? {
                                                          backgroundColor:
                                                              'action.hover',
                                                          borderRadius: 1,
                                                      }
                                                    : {},
                                        }}
                                    >
                                        <FormattedMessage id={translationKey} />
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>

                    <Box flex="1" overflow="hidden" minHeight={0}>
                        {activeStep === 0 && (
                            <StepSearchPatients
                                query={dicom.query}
                                setQuery={dicom.setQuery}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                onSearch={dicom.handleSearch}
                                isLoading={dicom.isLoading}
                                isFetching={dicom.isFetching}
                                isSearchPending={dicom.isSearchPending}
                                error={dicom.error}
                                filteredPatients={dicom.filteredPatients}
                                expandedPatients={expandedPatients}
                                onToggleExpanded={toggleExpanded}
                                onSelectPatient={handleSelectPatient}
                                availableTargets={dicom.availableTargets}
                                selectedTargetName={dicom.selectedTargetName}
                                onTargetChange={dicom.setSelectedTargetName}
                            />
                        )}

                        {activeStep === 1 && dicom.selectedPatient && (
                            <StepSelectSeries
                                selectedPatient={dicom.selectedPatient}
                                detailedPatient={dicom.detailedPatient}
                                selectedTargetName={dicom.selectedTargetName}
                                setSelectedTargetName={
                                    dicom.setSelectedTargetName
                                }
                                isLoadingStudies={dicom.isLoadingStudies}
                                isSeriesSelected={dicom.isSeriesSelected}
                                toggleSeriesSelection={
                                    dicom.toggleSeriesSelection
                                }
                                formatDate={dicom.formatDate}
                                selectedImagesCount={
                                    dicom.selectedImages.length
                                }
                                loading={dicom.loadingSeriesDownload}
                                availableTargets={dicom.availableTargets}
                                onBackToSearch={handleBackToSearch}
                                onDownload={() => {
                                    // Download not yet implemented
                                }}
                                onLoad={dicom.handleLoadSelectedSeries}
                            />
                        )}

                        {activeStep === 2 && (
                            <StepImportComplete
                                onBackToSelection={() => setActiveStep(1)}
                                onClose={handleModalClose}
                            />
                        )}
                    </Box>

                    <Box
                        display="flex"
                        justifyContent={
                            activeStep === 2 ? 'space-between' : 'flex-end'
                        }
                        pt={2}
                        mt={2}
                        borderTop="1px solid"
                        borderColor="divider"
                        flexShrink={0}
                    >
                        {activeStep === 2 ? (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={() => setActiveStep(1)}
                                >
                                    <FormattedMessage id="backToImageSelection" />
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleModalClose}
                                >
                                    <FormattedMessage id="close" />
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleModalClose}
                            >
                                <FormattedMessage id="cancel" />
                            </Button>
                        )}
                    </Box>
                </Box>
            </Modal>
        </>
    );
}
